import useMediaQuery from '@eversports/react-components/design-tokens/useMediaQuery'
import useTheme from '@eversports/react-components/design-tokens/useTheme'
import DateTimeRange from '@eversports/react-components/primitives/DateTimeRange'
import Flex from '@eversports/react-components/primitives/Flex'
import Heading from '@eversports/react-components/primitives/Heading'
import MonetaryValue from '@eversports/react-components/primitives/MonetaryValue'
import Text from '@eversports/react-components/primitives/Text'
import { assertNever } from '@eversports/utility-types'
import { makeStyles } from '@mui/styles'
import React from 'react'

import FormattedContent from '@eversports/react-components/primitives/FormattedContent'
import { Localized } from '../../localization/react'
import { getCurrentTicketPrice, PurchaseOption, TicketPriceType } from '../../typings/Product'
import Address from '../BookingDetails/common/Address'

import CardAvailabilitiesInformation from './CardAvailabilitiesInformation'
import CardUsagesInformation from './CardUsagesInformation'
import CardValidityInformation from './CardValidityInformation'
import MembershipModalContent from './MembershipModalContent'
import ValidActivities from './ValidActivities'
import ValidSports from './ValidSports'
import ValidVenues from './ValidVenues'
import HTMLFormattedContentWrapper, { onATag, phoenixAllowList } from './HTMLFormattedContentWrapper'

interface Props {
  product: PurchaseOption
}

const useStyles = makeStyles({
  strikethrough: {
    textDecoration: 'line-through',
  },
})

const ProductInformationDialogContent: React.FC<React.PropsWithChildren<Props>> = ({ product }) => {
  const theme = useTheme()
  const classes = useStyles()
  const isInMobileView = useMediaQuery(theme.breakpoints.down('md'))

  switch (product.__typename) {
    case 'Card':
      return (
        <Flex flexDirection="column" verticalSpacing="wide">
          <Heading is="h5" color="primary.main" variant={isInMobileView ? 'large' : 'x-large'}>
            <MonetaryValue value={product.price.value} currencyCode={product.price.currency} />
          </Heading>
          {product.usages.length > 0 && <CardUsagesInformation usages={product.usages} cardType={product.cardType} />}
          {product.validity && <CardValidityInformation validity={product.validity} />}
          <ValidVenues venues={product.venues} />
          <ValidSports sports={product.sports} />
          {product.availabilities.length > 0 && (
            <CardAvailabilitiesInformation availabilities={product.availabilities} />
          )}
          {product.activities.length > 0 && <ValidActivities activities={product.activities} />}
          {product.description && (
            <div>
              <Text size="small" color="darkGray">
                <Localized id="product-description" />
              </Text>
              <FormattedContent
                htmlContent={product.description}
                WrapperComponent={HTMLFormattedContentWrapper}
                allowList={phoenixAllowList}
                onTag={onATag}
              />
            </div>
          )}
        </Flex>
      )
    case 'Membership':
      return <MembershipModalContent membership={product} />
    case 'Ticket':
      const currentTicketPrice = getCurrentTicketPrice(product)
      return (
        <Flex flexDirection="column" verticalSpacing="wide">
          <div>
            <Heading is="h5" color="primary.main" variant={isInMobileView ? 'large' : 'x-large'}>
              <MonetaryValue value={currentTicketPrice.value} currencyCode={currentTicketPrice.currency} />
            </Heading>
            {product.currentPriceType !== TicketPriceType.TICKET_PRICE_TYPE_STANDARD && (
              <Text color="textGray">
                <Localized
                  id="ticket-regular-price"
                  params={{
                    price: (
                      <Text className={classes.strikethrough} color="textGray" is="span">
                        <MonetaryValue
                          value={product.standardPrice.value}
                          currencyCode={product.standardPrice.currency}
                        />
                      </Text>
                    ),
                  }}
                />
              </Text>
            )}
          </div>
          {product.currentPriceType === TicketPriceType.TICKET_PRICE_TYPE_EARLY_BIRD && product.earlyBirdUntil && (
            <div>
              <Text size="small" color="textGray">
                <Localized
                  id="ticket-early-bird-disclaimer"
                  params={{
                    earlyBirdValidUntil: (
                      <Text fontWeight="bold" size="small" is="span">
                        <Localized
                          id="ticket-early-bird-valid-until"
                          params={{ date: new Date(product.earlyBirdUntil) }}
                        />
                      </Text>
                    ),
                    regularPrice: (
                      <MonetaryValue
                        value={product.standardPrice.value}
                        currencyCode={product.standardPrice.currency}
                      />
                    ),
                  }}
                />
              </Text>
            </div>
          )}
          <div>
            <Text size="small" color="textGray" marginBottom="dense">
              <Localized id="tickets-included-sessions" />
            </Text>
            <Flex flexDirection="column" verticalSpacing="dense">
              {product.validForSessions.map((session) => (
                <div key={session.id}>
                  <Text size="small">
                    <DateTimeRange start={session.start} end={session.end} />
                  </Text>
                  {session.location && <Address location={session.location} variant="subtle" />}
                </div>
              ))}
            </Flex>
          </div>
        </Flex>
      )
    case 'SinglePurchase':
      return (
        <Flex flexDirection="column" verticalSpacing="wide">
          <Heading is="h5" color="primary.main" variant="x-large">
            <MonetaryValue value={product.price.value} currencyCode={product.price.currency} />
          </Heading>
        </Flex>
      )
    default:
      return assertNever(product)
  }
}

export default ProductInformationDialogContent
